import {createSlice, PayloadAction} from "@reduxjs/toolkit";
import _ from "lodash";
import {EventNotification} from "../../constants/type/EventNotification";

/**
 * Unread notifications shared by the header bell and every navigation menu.
 *
 * The STOMP subscription (owned by `UserMenu`) and the initial REST load both
 * push the unread list here so the bell badge and the per-menu badges always
 * come from the same source and update in real time.
 *
 * `seenIds` keeps the ids whose menu badge has already been cleared because the
 * user opened that menu. Those notifications stay unread server-side (they are
 * still listed in the bell and on "My notifications"): only the navigation
 * counter is reset, and a later notification for the same menu brings the badge
 * back.
 */
export interface NotificationState {
    unread: EventNotification[];
    seenIds: string[];
}

const initialState: NotificationState = {
    unread: [],
    seenIds: [],
};

const notificationSlice = createSlice({
    name: 'notification',
    initialState,
    reducers: {
        /** Replace the unread list (initial REST load or a real-time STOMP push). */
        setUnreadNotifications: (state, action: PayloadAction<EventNotification[]>) => {
            const unread = action.payload ?? [];
            state.unread = unread;
            // Drop ids that are no longer unread so the set cannot grow forever.
            const unreadIds = new Set(unread.map((n: any) => String(n?.id)));
            state.seenIds = state.seenIds.filter((id) => unreadIds.has(id));
        },
        /** A notification was read from the bell/panel/detail page. */
        removeUnreadNotification: (state, action: PayloadAction<string | number | null | undefined>) => {
            if (action.payload == null) {
                return;
            }
            const id = String(action.payload);
            state.unread = state.unread.filter((n: any) => String(n?.id) !== id);
            state.seenIds = state.seenIds.filter((seen) => seen !== id);
        },
        /**
         * Zero the badges of the given menu keys (the row that was opened plus
         * its ancestors/descendants). Matching notifications remain unread.
         */
        markMenuNotificationsSeen: (state, action: PayloadAction<{ cles?: string[]; liens?: string[] }>) => {
            const cles = new Set((action.payload?.cles ?? []).map((cle) => String(cle)));
            const liens = new Set(action.payload?.liens ?? []);
            if (cles.size === 0 && liens.size === 0) {
                return;
            }
            const seen = new Set(state.seenIds);
            (state.unread ?? []).forEach((n: any) => {
                const cle = !_.isEmpty(n?.uriErpCle) ? String(n.uriErpCle) : null;
                const matches = (cle != null && cles.has(cle)) || (!cle && liens.has(n?.uriErp));
                if (matches) {
                    seen.add(String(n.id));
                }
            });
            state.seenIds = Array.from(seen);
        },
        /** Reading in the backend failed for those ids: bring their badge back. */
        unmarkNotificationsSeen: (state, action: PayloadAction<Array<string | number>>) => {
            const ids = new Set((action.payload ?? []).map(String));
            if (ids.size === 0) {
                return;
            }
            state.seenIds = state.seenIds.filter((id) => !ids.has(id));
        },
        /** Logout: nothing must leak to the next user of this tab. */
        resetNotifications: () => initialState,
    },
});

export const {
    setUnreadNotifications,
    removeUnreadNotification,
    markMenuNotificationsSeen,
    unmarkNotificationsSeen,
    resetNotifications,
} = notificationSlice.actions;

export const selectUnreadNotifications = (state: any): EventNotification[] => state.notification.unread;
export const selectSeenNotificationIds = (state: any): string[] => state.notification.seenIds;

export default notificationSlice.reducer;
